// src/components/auth/SignUpForm.js
// Updated: 10/18/2025 11:12 PM

import React, { useState, useEffect } from 'react'; // Added useEffect
import { useNavigate, useSearchParams } from 'react-router-dom'; // Added useSearchParams
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth'; // Added signInWithEmailAndPassword
import { doc, setDoc } from 'firebase/firestore';
import { auth, db, functions } from '../../firebase/config'; // Added functions
import { httpsCallable } from 'firebase/functions'; // Added httpsCallable
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';

// Initialize the callable function
const signUpAndJoinFamilyCallable = httpsCallable(functions, 'signUpAndJoinFamily');

const SignUpForm = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // Hook to get URL params
  const [inviteCode, setInviteCode] = useState(null); // State to store invite code

  // Effect to read invite code from URL on component mount
  useEffect(() => {
    const code = searchParams.get('inviteCode');
    if (code) {
      setInviteCode(code);
      // Optional: Display a message indicating they are joining via invite
      toast(`Joining family with invite code: ${code}`);
    }
  }, [searchParams]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (inviteCode) {
        // --- NEW LOGIC: Use Cloud Function if invite code exists ---
        const result = await signUpAndJoinFamilyCallable({
          email,
          password,
          firstName,
          lastName,
          inviteCode,
        });

        // Sign in the user locally after successful creation by the function
        await signInWithEmailAndPassword(auth, email, password);

        toast.success(`Account created and joined '${result.data.familyName}' family!`);
        navigate('/'); // Redirect directly to homepage

      } else {
        // --- EXISTING LOGIC: Standard signup without invite code ---
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'users', user.uid), {
          firstName,
          lastName,
          email,
          profilePictureUrl: '',
          familyId: null, // User has not joined or created a family yet
        });

        toast.success('Account created! Please continue to onboarding.');
        navigate('/onboarding'); // Redirect to onboarding flow
      }
    } catch (error) {
        // Use error.message which often contains useful info from HttpsError
        toast.error(error.message || 'Sign up failed. Please try again.');
        console.error("Sign up error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignUp} className="space-y-6">
       {/* Form inputs remain the same... */}
       <div className="flex gap-4">
        <div className="w-1/2">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            First name
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
          />
        </div>
        <div className="w-1/2">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Last name
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="email-signup" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <input
          id="email-signup"
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="password-signup" className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <input
          id="password-signup"
          name="password"
          type="password"
          required
          minLength="6"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary sm:text-sm"
        />
      </div>

      <div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          {loading ? <Spinner size="sm" /> : 'Create account'}
        </button>
      </div>
    </form>
  );
};

export default SignUpForm;